/**
 * UncollectInvoiceModal — deixar de cobrar uma fatura, sem cancelá-la.
 *
 * O motivo é obrigatório: o billing o grava como motivo da suspensão da cobrança
 * (`collectionSuspendedReason`), que aparece no detalhe da fatura e nos fatos da régua de cobrança
 * (AF-3940). Acordo comercial, recompra, recuperação judicial e ação judicial são os casos comuns.
 */
import { useState } from 'react';
import { toast } from 'react-toastify';
import { FaTimes, FaPauseCircle } from 'react-icons/fa';
import { uncollectInvoice } from '../services/billingApi';
import { formatBRL } from '../../../../utils/currencyUtils';

interface Props {
    invoice: any;
    onClose: () => void;
    onSuccess: () => void;
}

const UncollectInvoiceModal: React.FC<Props> = ({ invoice, onClose, onSuccess }) => {
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleConfirm = async () => {
        if (!reason.trim()) return toast.warn('Informe o motivo para deixar de cobrar');
        setSubmitting(true);
        try {
            const res = await uncollectInvoice(invoice.invoiceId ?? invoice.id, reason.trim());
            toast.success(res.result === 'ALREADY_UNCOLLECTED'
                ? 'A fatura já estava fora da cobrança'
                : 'A fatura deixou de ser cobrada');
            onSuccess();
            onClose();
        } catch (e: any) {
            toast.error(e?.message || 'Falha ao deixar de cobrar');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-md w-full">
                <div className="flex justify-between items-center mb-3">
                    <h2 className="text-lg font-semibold flex items-center gap-2 dark:text-white">
                        <FaPauseCircle className="text-slate-500" /> Deixar de cobrar
                    </h2>
                    <button onClick={onClose} aria-label="Fechar"
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><FaTimes size={20} /></button>
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Fatura {String(invoice.invoiceId).slice(0, 8)} · {formatBRL(invoice.amount)}
                    {' · '}a fatura sai da cobrança, mas continua devida.
                </p>

                <label className="block text-sm font-medium mb-1 dark:text-gray-200" htmlFor="motivo-descobranca">
                    Motivo
                </label>
                <textarea
                    id="motivo-descobranca"
                    className="w-full text-sm border rounded p-2 mb-3 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    rows={2}
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder="Ex.: acordo comercial, recompra, recuperação judicial"
                />

                <div className="flex gap-2 justify-end">
                    <button onClick={onClose} disabled={submitting}
                        className="px-4 py-2 text-sm rounded bg-gray-200 dark:bg-slate-600 dark:text-white hover:bg-gray-300">
                        Voltar
                    </button>
                    <button onClick={handleConfirm} disabled={submitting || !reason.trim()}
                        className="px-4 py-2 text-sm rounded bg-slate-600 text-white hover:bg-slate-700 disabled:opacity-60">
                        {submitting ? 'Salvando...' : 'Deixar de cobrar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UncollectInvoiceModal;
